import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useApp } from '@/app/AppContext'
import { can } from '@/auth/permissions'
import { Icon, type IconName } from '@/components/Icon'
import { MoreMenu, StateView, type MenuAction } from '@/card/record'
import { ApiError } from '@/lib/api'
import '@/styles/admin.css'

/**
 * Общий каркас экранов администрирования (тикет #97, макеты AdmUsers … AdmFields).
 *
 * Своя обвязка, а не взятая у отчётов: у раздела другой вопрос к пустоте и к отказу. Пустой
 * справочник — это не «данных нет», а признак несработавшей миграции, а отказ — почти всегда
 * отсутствие права раздела, и называть его надо словом, а не пустой таблицей.
 *
 * Режим «только просмотр» (твик `access=readonly` макетов) — баннер над содержимым, и ни
 * одной кнопки правки: ни «Добавить», ни «⋯», ни ручек перетаскивания, ни переключателей.
 * Кнопка, нарисованная и ведущая к гарантированному 403, учит игнорировать отказы.
 */

export const isForbidden = (error: unknown) => error instanceof ApiError && error.isForbidden

export function AdminPage({
  titleKey,
  count,
  leadKey,
  primary,
  readOnly,
  roKey = 'adm.ro.text',
  forbidden,
  headExtra,
  sectionKey = 'nav.section.admin',
  children,
}: {
  titleKey: string
  /** Число рядом с заголовком («86», «8 правил»). */
  count?: ReactNode
  leadKey?: string
  /** Основное действие экрана — одно, справа в шапке. */
  primary?: ReactNode
  /** Нет права правки: сверху баннер, кнопок правки нет. */
  readOnly?: boolean
  roKey?: string
  /** Раздел закрыт: ни описания, ни баннера, ни действий — только «Нет доступа». */
  forbidden?: boolean
  headExtra?: ReactNode
  /** Раздел меню в «хлебных крошках»: каркас служит и экранам вне администрирования (склады). */
  sectionKey?: string
  children: ReactNode
}) {
  const { t } = useApp()
  return (
    <>
      <nav className="crumbs" aria-label={t('record.crumbs')}>
        <span>{t(sectionKey)}</span>
        <span aria-hidden="true">/</span>
        <span aria-current="page">{t(titleKey)}</span>
      </nav>
      <div className="page-head">
        <h1 className="h1">{t(titleKey)}</h1>
        {!forbidden && count !== undefined && count != null && <span className="muted t-md">{count}</span>}
        <span className="spacer" />
        {!forbidden && headExtra}
        {!forbidden && primary}
      </div>
      {!forbidden && leadKey && <p className="page-lead">{t(leadKey)}</p>}
      {!forbidden && readOnly && (
        <div className="ro-banner" role="note">
          <Icon name="lock" />
          <span>
            <span className="strong">{t('adm.ro.title')}</span> {t(roKey)}
          </span>
        </div>
      )}
      {children}
    </>
  )
}

/** Основная кнопка шапки: «Добавить …». */
export function PrimaryButton({ label, onClick, icon = 'plus', quiet }: {
  label: string
  onClick: () => void
  icon?: IconName
  /** Есть несохранённые правки — основная кнопка экрана сейчас «Сохранить», эта уступает. */
  quiet?: boolean
}) {
  return (
    <button type="button" className={quiet ? 'btn btn-secondary' : 'btn btn-primary'} onClick={onClick}>
      <Icon name={icon} />
      {label}
    </button>
  )
}

/**
 * Загрузка, отказ и ошибка одного запроса.
 *
 * Загрузка — скелетон той же формы, что данные: страница не прыгает, когда они приходят.
 * Отказ сервера показывается его словами — там названа причина, а общая «ошибка» отправила
 * бы администратора гадать.
 */
export function AdminState({
  isLoading,
  error,
  onRetry,
  skeleton,
  forbiddenTitleKey = 'adm.state.forbidden.title',
  errorTitleKey = 'adm.state.error.title',
  children,
}: {
  isLoading: boolean
  error: unknown
  onRetry?: () => void
  skeleton?: ReactNode
  forbiddenTitleKey?: string
  errorTitleKey?: string
  children: ReactNode
}) {
  const { t } = useApp()
  if (isLoading) return <>{skeleton ?? <SkeletonTable columns={5} />}</>
  if (error) {
    if (isForbidden(error)) {
      return (
        <StateView
          boxed
          kind="forbidden"
          title={t(forbiddenTitleKey)}
          description={t('adm.state.forbidden.text')}
          actions={<Link className="btn btn-ghost btn-sm" to="/">{t('adm.state.home')}</Link>}
        />
      )
    }
    return (
      <StateView
        boxed
        kind="error"
        title={t(errorTitleKey)}
        description={error instanceof ApiError ? error.message : t('adm.state.error.text')}
        actions={
          onRetry && (
            <button type="button" className="btn btn-primary btn-sm" onClick={onRetry}>
              <Icon name="refresh" size="sm" />
              {t('adm.retry')}
            </button>
          )
        }
        code={error instanceof ApiError ? String(error.status) : undefined}
      />
    )
  }
  return <>{children}</>
}

/** Скелетон таблицы: столько колонок, сколько у данных. */
export function SkeletonTable({ columns, rows = 8, label }: { columns: number; rows?: number; label?: string }) {
  const { t } = useApp()
  return (
    <div className="tbl-wrap" aria-busy="true" aria-label={label ?? t('app.loading')}>
      <table className="tbl">
        <tbody>
          {Array.from({ length: rows }, (_, row) => (
            <tr key={row}>
              {Array.from({ length: columns }, (_, col) => (
                <td key={col}>
                  <span className="skel" style={{ width: `${50 + ((row * 23 + col * 37) % 90)}px` }} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Скелетон сортируемого списка (причины, этапы, правила). */
export function SkeletonList({ rows = 6 }: { rows?: number }) {
  const { t } = useApp()
  return (
    <div className="sort-list" aria-busy="true" aria-label={t('app.loading')}>
      {Array.from({ length: rows }, (_, row) => (
        <div className="sort-row" key={row} style={{ paddingLeft: 10 }}>
          <span className="skel skel-c" style={{ width: 22, height: 22 }} />
          <span className="skel" style={{ width: `${40 + ((row * 23) % 40)}%` }} />
          <span className="spacer" />
          <span className="skel" style={{ width: 64 }} />
        </div>
      ))}
    </div>
  )
}

/**
 * Сообщение об отказе сервера и право на правку одним местом.
 *
 * Отказ показывается текстом сервера: там названа причина («номера повторятся в
 * январе»), а общая «ошибка сохранения» отправила бы администратора гадать.
 */
export function useAdminEditing(resource = 'system.settings') {
  const { t, user } = useApp()
  const [notice, setNotice] = useState<string | null>(null)
  return {
    // Пользователи и роли (#102) правятся своими правами — system.user и system.role, —
    // а не system.settings: настраивать справочники и раздавать доступ — разные обязанности.
    mayEdit: can(user, resource, 'update'),
    notice,
    clear: () => setNotice(null),
    fail: (error: unknown) =>
      setNotice(error instanceof ApiError ? error.message : t('app.error')),
  }
}

export function Notice({ text }: { text: string | null }) {
  if (!text) return null
  return (
    <div className="alert alert-danger" role="alert">
      <Icon name="alert" />
      <span>{text}</span>
    </div>
  )
}

/**
 * Слой правки поверх экрана: боковая панель (`sheet`) или окно (`modal`).
 *
 * Esc закрывает без сохранения, фокус при открытии — на первом поле, после закрытия
 * возвращается туда, откуда открыли: иначе клавиатурный пользователь оказывается в начале
 * страницы.
 */
export function EditLayer({
  kind,
  overline,
  title,
  subtitle,
  onClose,
  footer,
  hint,
  wide,
  large = true,
  children,
}: {
  kind: 'sheet' | 'modal'
  overline?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  onClose: () => void
  footer: ReactNode
  /** Подсказка слева в подвале панели («Esc — закрыть без сохранения»). */
  hint?: ReactNode
  wide?: boolean
  large?: boolean
  children: ReactNode
}) {
  const { t } = useApp()
  const titleId = useId()
  const ref = useRef<HTMLElement>(null)
  const closeRef = useRef(onClose)
  closeRef.current = onClose

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null
    const first = ref.current?.querySelector<HTMLElement>(
      'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])',
    )
    ;(first ?? ref.current)?.focus()
    return () => opener?.focus?.()
  }, [])

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.stopPropagation()
      closeRef.current()
    }
  }

  const head = (
    <>
      <div className="stack" style={{ flex: 1, gap: 2 }}>
        {overline && <span className="overline">{overline}</span>}
        <span id={titleId} className="h2" style={kind === 'modal' ? { fontSize: 15 } : undefined}>
          {title}
        </span>
        {subtitle && <span className="t-sm muted">{subtitle}</span>}
      </div>
      <button type="button" className="btn btn-ghost btn-sm btn-icon" aria-label={t('adm.close')} onClick={onClose}>
        <Icon name="close" size="sm" />
      </button>
    </>
  )

  if (kind === 'sheet') {
    return (
      <div className="overlay is-sheet adm-overlay" onKeyDown={onKeyDown}>
        <aside
          ref={ref}
          tabIndex={-1}
          className={wide ? 'sheet is-wide' : 'sheet'}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          <div className="sheet-h">{head}</div>
          <div className="sheet-b">{children}</div>
          <div className="sheet-f">
            <span className="hint">{hint ?? t('adm.escHint')}</span>
            {footer}
          </div>
        </aside>
      </div>
    )
  }
  return (
    <div className="overlay is-modal adm-overlay" onKeyDown={onKeyDown}>
      <div
        ref={ref as React.RefObject<HTMLDivElement>}
        tabIndex={-1}
        className={large ? 'modal modal-lg' : 'modal'}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="modal-h adm-modal-h">{head}</div>
        <div className="modal-b is-form">{children}</div>
        <div className="modal-f">{footer}</div>
      </div>
    </div>
  )
}

/** Кнопки подвала слоя правки: «Отмена» и одна основная. */
export function LayerButtons({
  onCancel,
  saveLabel,
  pending,
  disabled,
  form,
}: {
  onCancel: () => void
  saveLabel: string
  pending?: boolean
  disabled?: boolean
  /** id формы, если кнопка стоит вне её (подвал панели). */
  form?: string
}) {
  const { t } = useApp()
  return (
    <>
      <button type="button" className="btn btn-secondary" onClick={onCancel}>
        {t('admin.cancel')}
      </button>
      <button type="submit" form={form} className="btn btn-primary" disabled={pending || disabled}>
        {pending ? t('adm.saving') : saveLabel}
      </button>
    </>
  )
}

/**
 * Несохранённые изменения (настройки, матрица прав): одна кнопка сохранения на экран, и
 * каждое изменение названо «было → стало». Ошибка формы делает панель красной, а сохранение
 * недоступным — до сервера уходит только то, что он не отвергнет по очевидной причине.
 */
export function SaveBar({
  changes,
  onReset,
  onSave,
  pending,
  invalid,
  note,
  saveLabel,
}: {
  changes: string[]
  onReset: () => void
  onSave: () => void
  pending?: boolean
  /** Текст ошибки формы — сохранение недоступно, пока её не исправят. */
  invalid?: string | null
  note?: ReactNode
  saveLabel?: string
}) {
  const { t } = useApp()
  if (changes.length === 0) return null
  return (
    <div className="savebar" role="region" aria-label={t('adm.savebar')}
         style={invalid ? { borderColor: 'var(--danger-border)' } : undefined}>
      <span className="dot" aria-hidden="true" style={invalid ? { background: 'var(--danger)' } : undefined} />
      <span className="strong">{t('adm.savebar.count', { count: changes.length })}</span>
      <span className="changes">
        {changes.map((change) => (
          <span key={change} className="chg">{change}</span>
        ))}
      </span>
      {invalid && (
        <span className="flag flag-danger">
          <Icon name="alert" />
          {invalid}
        </span>
      )}
      {note && <span className="t-sm muted">{note}</span>}
      <span className="spacer" />
      <button type="button" className="btn btn-ghost btn-sm" onClick={onReset} disabled={pending}>
        {t('adm.savebar.reset')}
      </button>
      <button type="button" className="btn btn-primary btn-sm" onClick={onSave} disabled={pending || Boolean(invalid)}>
        {pending ? t('adm.saving') : (saveLabel ?? t('admin.save'))}
      </button>
    </div>
  )
}

/** Переключатель всегда с подписью: цвет дорожки — не единственный носитель смысла. */
export function Switch({
  checked,
  onChange,
  label,
  ariaLabel,
  disabled,
}: {
  checked: boolean
  onChange: (value: boolean) => void
  label?: ReactNode
  ariaLabel?: string
  disabled?: boolean
}) {
  const { t } = useApp()
  return (
    <label className="switch">
      <input type="checkbox" checked={checked} disabled={disabled} aria-label={ariaLabel}
             onChange={(event) => onChange(event.target.checked)} />
      <span className="track" />
      <span className="sw-label">{label ?? (checked ? t('adm.on') : t('adm.off'))}</span>
    </label>
  )
}

/** То же значение в режиме чтения: бейдж с иконкой и словом. */
export function OnOffBadge({ on }: { on: boolean }) {
  const { t } = useApp()
  return on ? (
    <span className="badge b-success"><Icon name="check" />{t('adm.enabled')}</span>
  ) : (
    <span className="badge"><Icon name="close" />{t('adm.disabled')}</span>
  )
}

/** «⋯» строки таблицы или списка. */
export function RowMenu({ label, items }: { label: string; items: MenuAction[] }) {
  return <MoreMenu compact label={label} items={items} />
}

export function SearchBox({ value, onChange, label, placeholder, width = 240 }: {
  value: string
  onChange: (value: string) => void
  label: string
  placeholder?: string
  width?: number
}) {
  return (
    <div className="input-icon" style={{ width }}>
      <Icon name="search" />
      <input className="input input-sm" aria-label={label} placeholder={placeholder ?? label} value={value}
             onChange={(event) => onChange(event.target.value)} />
    </div>
  )
}

/** Поле формы: подпись, контрол, ошибка под полем или подсказка. */
export function Field({
  label,
  htmlFor,
  help,
  error,
  dirty,
  required,
  span2,
  children,
}: {
  label: ReactNode
  htmlFor?: string
  help?: ReactNode
  error?: ReactNode
  dirty?: boolean
  required?: boolean
  span2?: boolean
  children: ReactNode
}) {
  const classes = ['field', dirty ? 'is-dirty' : '', span2 ? 'span-2' : ''].filter(Boolean).join(' ')
  const caption = (
    <>
      {label}
      {required && <span className="req" aria-hidden="true"> *</span>}
    </>
  )
  return (
    <div className={classes}>
      {htmlFor ? <label className="label" htmlFor={htmlFor}>{caption}</label> : <span className="label">{caption}</span>}
      {children}
      {error ? (
        <span className="err-text"><Icon name="adm-error" size="sm" />{error}</span>
      ) : (
        help && <span className="help">{help}</span>
      )}
    </div>
  )
}

/** Значение в режиме чтения на месте поля. */
export function ReadonlyValue({ children, mono }: { children: ReactNode; mono?: boolean }) {
  return (
    <div className={mono ? 'input is-readonly mono' : 'input is-readonly'} aria-readonly="true">
      {children}
      <span className="spacer" />
      <Icon name="lock" size="sm" />
    </div>
  )
}

// ------------------------------------------------------------------ палитра меток

/**
 * Палитра меток (CompExtra, `--tag-*`). Сервер хранит цвет как `#RRGGBB`, поэтому выбор
 * сохраняется значением светлой темы, а показывается токеном — в тёмной теме тот же цвет
 * берёт свой вариант. Цвет, заданный вручную до палитры, показывается как есть.
 */
export const TAG_COLORS = [
  { name: 'gray', hex: '#7D8086' },
  { name: 'blue', hex: '#3566D6' },
  { name: 'teal', hex: '#127F86' },
  { name: 'green', hex: '#2A8550' },
  { name: 'amber', hex: '#B97D0B' },
  { name: 'orange', hex: '#C85A22' },
  { name: 'red', hex: '#C23A2E' },
  { name: 'purple', hex: '#7550C2' },
] as const

export function colorToken(hex: string | null | undefined): string | null {
  if (!hex) return null
  const known = TAG_COLORS.find((color) => color.hex.toLowerCase() === hex.toLowerCase())
  return known ? `var(--tag-${known.name})` : hex
}

export function colorName(t: (key: string) => string, hex: string | null | undefined): string {
  const known = TAG_COLORS.find((color) => color.hex.toLowerCase() === (hex ?? '').toLowerCase())
  return known ? t(`adm.color.${known.name}`) : (hex ?? t('adm.color.none'))
}

export function SwatchPick({ value, onChange, labelledBy }: {
  value: string
  onChange: (hex: string) => void
  labelledBy?: string
}) {
  const { t } = useApp()
  const custom = value && !TAG_COLORS.some((color) => color.hex.toLowerCase() === value.toLowerCase())
  return (
    <div className="swatch-pick" role="group" aria-labelledby={labelledBy}>
      {TAG_COLORS.map((color) => (
        <button key={color.name} type="button" aria-label={t(`adm.color.${color.name}`)}
                aria-pressed={value.toLowerCase() === color.hex.toLowerCase()} onClick={() => onChange(color.hex)}>
          <span style={{ background: `var(--tag-${color.name})` }} />
        </button>
      ))}
      {custom && (
        <button type="button" aria-label={value} aria-pressed="true" onClick={() => onChange(value)}>
          <span style={{ background: value }} />
        </button>
      )}
    </div>
  )
}

/** Метка: точка цвета и название — цвет не единственный носитель. */
export function TagChip({ name, color }: { name: ReactNode; color: string | null | undefined }) {
  return (
    <span className="tag">
      <span className="sw" style={{ background: colorToken(color) ?? 'var(--tag-gray)' }} />
      {name}
    </span>
  )
}

/** Условие словами: поле · оператор · значение (`.ftoken`). */
export function Token({ k, op, v }: { k: ReactNode; op: ReactNode; v: ReactNode }) {
  return (
    <span className="ftoken">
      <span className="k">{k}</span>
      <span className="op">{op}</span>
      <span className="v">{v}</span>
    </span>
  )
}

export function Tokens({ items, join }: { items: { k: ReactNode; op: ReactNode; v: ReactNode }[]; join: string }) {
  return (
    <span className="row-wrap" style={{ gap: '4px 6px' }}>
      {items.map((item, index) => (
        <Fragment key={index}>
          {index > 0 && <span className="t-xs muted">{join}</span>}
          <Token {...item} />
        </Fragment>
      ))}
    </span>
  )
}

/** Инициалы для аватара: «Иванов Иван» → «ИИ». */
export function initials(name: string | null | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean)
  return parts.slice(0, 2).map((part) => part[0]?.toUpperCase() ?? '').join('') || '··'
}

/** Ручка перетаскивания с клавиатуры: ↑/↓ двигают на одно место. */
export function Grip({ label, onMove, disabled, locked }: {
  label: string
  onMove?: (step: -1 | 1) => void
  disabled?: boolean
  /** Закреплённая строка: ручка видна, но не двигает (замок вместо точек). */
  locked?: boolean
}) {
  return (
    <button
      type="button"
      className="grip"
      aria-label={label}
      disabled={disabled || locked}
      onKeyDown={(event) => {
        if (!onMove) return
        if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
          event.preventDefault()
          onMove(event.key === 'ArrowUp' ? -1 : 1)
        }
      }}
    >
      <Icon name={locked ? 'lock' : 'adm-grip'} size="sm" />
    </button>
  )
}

/** Перестановка: элемент `id` встаёт на место `target` (индекс в исходном порядке). */
export function moveId(ids: string[], id: string, target: number): string[] {
  const rest = ids.filter((value) => value !== id)
  rest.splice(Math.max(0, Math.min(target, rest.length)), 0, id)
  return rest
}
